import React, { useMemo, useState } from "react";
import {
  MoreVertical,
  Pencil,
  Search,
  Trash2,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function DraftsList({
  drafts,
  onCreateNew,
  onEdit,
  onDelete,
}) {
  const [query, setQuery] = useState("");
  const [menuId, setMenuId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return drafts;
    return drafts.filter(
      (f) =>
        f.name.toLowerCase().includes(q) ||
        f.typeLabel?.toLowerCase().includes(q) ||
        f.createdBy?.toLowerCase().includes(q)
    );
  }, [drafts, query]);

  return (
    <div className="px-6 py-6 text-left">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-[#1D2739]">Drafts</h2>
        <button
          type="button"
          onClick={onCreateNew}
          className="rounded-lg bg-[#EAF524] px-4 py-2 text-sm font-semibold text-black hover:bg-[#e0eb1f]"
        >
          + Create new form
        </button>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="w-[320px] rounded-lg bg-gray-100 py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <button
          type="button"
          className="rounded-lg bg-blue-500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-600"
        >
          Search
        </button>
      </div>

      {filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500">
          No draft forms yet. Create a new form to get started.
        </p>
      ) : (
        <div className="overflow-visible rounded-xl border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-white text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Form Name</th>
                <th className="px-4 py-3 font-medium">Created By</th>
                <th className="px-4 py-3 font-medium">Date Created</th>
                <th className="px-4 py-3 font-medium">Last Modified</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((form) => (
                <tr
                  key={form.id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60"
                >
                  <td className="px-4 py-4 font-medium text-[#1D2739]">
                    {form.name}
                  </td>
                  <td className="px-4 py-4 text-gray-600">{form.createdBy}</td>
                  <td className="px-4 py-4 text-gray-600">
                    {formatDate(form.createdAt)}
                  </td>
                  <td className="px-4 py-4 text-gray-600">
                    {formatDate(form.updatedAt)}
                  </td>
                  <td className="px-4 py-4">
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                      Draft
                    </span>
                  </td>
                  <td className="relative px-4 py-4 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        setMenuId(menuId === form.id ? null : form.id)
                      }
                      className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {menuId === form.id && (
                      <div className="absolute right-4 z-20 mt-1 w-44 rounded-lg border border-gray-100 bg-white py-1 shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            onEdit(form.id);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Pencil className="h-4 w-4" />
                          Continue Editing
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuId(null);
                            onDelete(form.id);
                          }}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
